import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useTaillightsController, useTaillightsRendering} from '@github-ui/taillights/provider'
import {sourceChunks} from '@github-ui/taillights-worker/source-chunks'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {
  gutterFocusScrollTop,
  initialWindowChunks,
  layeredChunks,
  nextScrollSample,
  sameSourceWindow,
  type ScrollSample,
  sourceBlockAt,
  type SourceWindow,
  sourceWindowForViewport,
  windowedSourceChunkLines,
} from './source-window'
import {SourceChunk, sourceRenderingStyle} from './SourceChunk'
import styles from './TaillightsCodeView.module.css'
import type {SourceChunksProps} from './WindowedSource'

const leadIdleMs = 120
// Content moving under a resting pointer would otherwise hit-test and restyle hovered rows every frame.
const scrollingPointerMs = 100
const noLayers: ReadonlySet<number> = new Set()

type Props = Omit<
  SourceChunksProps,
  'linesPerChunk' | 'linesToBuffer' | 'virtualized' | 'plaintext' | 'reverseStickyRefs'
> & {reverseStickyRefs: NonNullable<SourceChunksProps['reverseStickyRefs']>}

function clearInverseStickyLayout(refs: Props['reverseStickyRefs']) {
  refs.offsetRef.current?.style.removeProperty('height')
  const sticky = refs.windowRef.current
  sticky?.style.removeProperty('top')
  sticky?.style.removeProperty('bottom')
  sticky?.removeAttribute('data-sticky-ready')
  sticky?.removeAttribute('data-scrolling')
  refs.sourceRef.current?.style.removeProperty('transform')
}

export function InverseStickySourceChunks({
  view,
  projection,
  renderer,
  safariCSSHighlighting,
  gutter,
  focus,
  scrollMargin,
  scrollPaddingTop = 0,
  preparedFor,
  onRenderError,
  reverseStickyRefs,
}: Props) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const {isBrowser} = useRuntimeEnvironment()
  const profile = preparedFor?.profile ?? controller?.profile
  const lineHeight = profile?.lineHeight ?? rendering?.lineHeight ?? 20
  const fallbackStyle = useMemo(() => sourceRenderingStyle(profile, rendering), [profile, rendering])
  const blocks = useMemo(
    () => view?.blocks ?? sourceChunks(projection.lineNumbers, windowedSourceChunkLines),
    [view, projection.lineNumbers],
  )
  const chunksRef = useRef<HTMLDivElement>(null)
  const sampledWindowRef = useRef<SourceWindow | undefined>(undefined)
  const scrollSampleRef = useRef<ScrollSample | undefined>(undefined)
  const idleTimerRef = useRef<number | undefined>(undefined)
  const scrollingTimerRef = useRef<number | undefined>(undefined)
  const {focusedLine, request: focusRequest, onFocusChange} = focus
  const handledFocusRef = useRef(focusRequest)
  const [attachmentRevision, setAttachmentRevision] = useState(0)
  const [range, setRange] = useState<SourceWindow>(() => ({
    first: 0,
    last: Math.min(initialWindowChunks, blocks.length) - 1,
    entry: 0,
    entryLast: Math.min(initialWindowChunks, blocks.length) - 1,
    viewportHeight: 0,
  }))
  const [layered, setLayered] = useState(noLayers)
  const ownerLine = gutter.actions.owner?.lineNumber
  const first = Math.min(range.first, blocks.length - 1)
  const last = Math.min(range.last, blocks.length - 1)
  const entry = Math.min(range.entry, blocks.length - 1)
  const entryLast = Math.min(range.entryLast, blocks.length - 1)
  const mounted = Array.from({length: Math.max(0, last - first + 1)}, (_, index) => first + index)
  for (const line of [focusedLine, ownerLine]) {
    if (line == null) continue
    const display = projection.sourceToDisplay.get(line - 1)
    if (display === undefined) continue
    const index = sourceBlockAt(blocks, display)
    if (!mounted.includes(index)) mounted.push(index)
  }
  mounted.sort((a, b) => a - b)
  const start = (blocks[first]?.displayStart ?? 0) * lineHeight
  const endBlock = blocks[last]
  const end = endBlock ? (endBlock.displayStart + Math.max(1, endBlock.lineNumbers.length)) * lineHeight : lineHeight
  const height = Math.max(lineHeight, end - start)
  // Keep React's mount height constant; the layout effect owns live height and transform.
  const [initialHeight] = useState(height)
  const attachChunks = useCallback(
    (node: HTMLDivElement) => {
      chunksRef.current = node
      // Reattachment can reuse the same node and range after Activity/Suspense hides it.
      setAttachmentRevision(previous => previous + 1)
      return () => {
        // Ref cleanup precedes the next layout effects, including synchronous print materialization.
        clearInverseStickyLayout(reverseStickyRefs)
        chunksRef.current = null
      }
    },
    [reverseStickyRefs],
  )

  const commitWindow = useEffectEvent((lead: number, settled = false) => {
    const scrollTop = window.scrollY - scrollMargin
    const viewportHeight = window.innerHeight
    const next = sourceWindowForViewport(blocks, lineHeight, scrollTop, viewportHeight, lead)
    const previous = sampledWindowRef.current
    // Release every chunk layer once scrolling settles.
    if (settled) setLayered(current => (current.size ? noLayers : current))
    if (previous && sameSourceWindow(previous, next)) return
    sampledWindowRef.current = next
    if (!settled) {
      setLayered(current =>
        layeredChunks(
          current,
          blocks,
          previous,
          next,
          lead !== 0,
          scrollTop / lineHeight,
          (scrollTop + viewportHeight) / lineHeight,
        ),
      )
    }
    setRange(current => (sameSourceWindow(current, next) ? current : next))
  })

  const sampleViewport = useEffectEvent((event?: Event) => {
    const scrollTop = window.scrollY - scrollMargin
    // The event's timestamp marks when the scroll offset changed, so a handler delayed within a frame still measures
    // a steady speed.
    const sample = nextScrollSample(
      scrollSampleRef.current,
      event?.timeStamp ?? performance.now(),
      scrollTop,
      window.innerHeight,
      lineHeight,
    )
    scrollSampleRef.current = sample
    const sticky = reverseStickyRefs.windowRef.current
    if (event?.type === 'scroll' && sticky) {
      if (!sticky.hasAttribute('data-scrolling')) sticky.setAttribute('data-scrolling', '')
      window.clearTimeout(scrollingTimerRef.current)
      scrollingTimerRef.current = window.setTimeout(() => sticky.removeAttribute('data-scrolling'), scrollingPointerMs)
    }
    window.clearTimeout(idleTimerRef.current)
    // Drop the lead and any chunk layers once scrolling settles.
    idleTimerRef.current = window.setTimeout(() => {
      scrollSampleRef.current = undefined
      commitWindow(0, true)
    }, leadIdleMs)
    // Steady scrolls (holding the scrollbar track, Page Down) commit their read-ahead window in the frame that scrolled,
    // so the slice keeps pace. Other scrolls keep committing a frame later: committing drags and jumps in the same frame
    // shows many more blank rows.
    // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- A deferred render paints the read-ahead a frame late.
    if (event && sample.lead !== 0) flushSync(() => commitWindow(sample.lead))
    else commitWindow(sample.lead)
  })

  useEffect(() => {
    // eslint-disable-next-line github/prefer-observers -- Distant jumps need native page offsets, not delayed intersection delivery.
    window.addEventListener('scroll', sampleViewport)
    // eslint-disable-next-line github/prefer-observers -- Window extent determines the inverse-sticky insets.
    window.addEventListener('resize', sampleViewport)
    sampleViewport()
    return () => {
      window.clearTimeout(idleTimerRef.current)
      window.clearTimeout(scrollingTimerRef.current)
      window.removeEventListener('scroll', sampleViewport)
      window.removeEventListener('resize', sampleViewport)
    }
  }, [])

  useLayoutEffect(() => {
    if (!isBrowser) return
    if (range.viewportHeight === 0) {
      sampleViewport()
      return
    }
    const offset = reverseStickyRefs.offsetRef.current
    const sticky = reverseStickyRefs.windowRef.current
    const source = reverseStickyRefs.sourceRef.current
    const chunks = chunksRef.current
    // Ancestor refs attach later in this commit; the attachment update retries before paint.
    if (!offset || !sticky || !source || !chunks) return
    const viewportHeight = window.innerHeight
    const actualScrollY = window.scrollY
    const viewport = sticky.firstElementChild
    const innerScrollTop = viewport instanceof HTMLElement ? viewport.scrollTop : 0
    let targetScrollY = actualScrollY
    const reveal = focusRequest && focusRequest !== handledFocusRef.current
    if (reveal) {
      const display = projection.sourceToDisplay.get(focusRequest.line - 1)
      if (display !== undefined) {
        const rowTop = scrollMargin + display * lineHeight
        targetScrollY = gutterFocusScrollTop(rowTop, lineHeight, scrollPaddingTop, actualScrollY, viewportHeight)
      }
    }
    const lead = reveal ? 0 : (scrollSampleRef.current?.lead ?? 0)
    const scrollTop = targetScrollY - scrollMargin
    const next = sourceWindowForViewport(blocks, lineHeight, scrollTop, viewportHeight, lead)
    // Reconciliation can change the window without a scroll event.
    sampledWindowRef.current = next
    // Correct a stale scroll/fold snapshot before paint, without positioning it as the new slice.
    if (!sameSourceWindow(range, next)) {
      // Read-ahead chunks this correction mounts need layers just like sampled ones.
      setLayered(current =>
        layeredChunks(
          current,
          blocks,
          range,
          next,
          lead !== 0,
          scrollTop / lineHeight,
          (scrollTop + viewportHeight) / lineHeight,
        ),
      )
      setRange(next)
      return
    }
    const inset = Math.min(0, viewportHeight - height)
    // A slice pinned to the bottom stops early enough that its bottom edge stays below the screen. Before hydration
    // that edge carries the code's own horizontal scrollbar, which should only show at the end of the file; after
    // it, the sticky scrollbar takes over.
    const scrollbarEdge = viewport instanceof HTMLElement ? viewport.offsetHeight - viewport.clientHeight : 0
    const topInset = Math.min(0, viewportHeight - height + Math.max(lineHeight, scrollbarEdge))
    const translate = `translateY(${-start}px)`
    const write = (node: HTMLElement, key: string, value: string) => {
      if (node.style.getPropertyValue(key) !== value) node.style.setProperty(key, value)
    }
    // Update live geometry together, only after the committed slice matches.
    write(offset, 'height', `${start}px`)
    write(sticky, 'top', `${topInset}px`)
    write(sticky, 'bottom', `${inset}px`)
    write(source, 'transform', translate)
    write(chunks, 'height', `${height}px`)
    write(chunks, 'transform', translate)
    if (!sticky.hasAttribute('data-sticky-ready')) sticky.setAttribute('data-sticky-ready', 'true')
    if (innerScrollTop && viewport instanceof HTMLElement) viewport.scrollTo({top: 0, behavior: 'instant'})
    if (targetScrollY !== actualScrollY) window.scrollTo({top: targetScrollY, behavior: 'instant'})
    if (reveal) handledFocusRef.current = focusRequest
  }, [
    isBrowser,
    attachmentRevision,
    reverseStickyRefs,
    blocks,
    lineHeight,
    scrollMargin,
    scrollPaddingTop,
    range,
    start,
    height,
    projection,
    focusRequest,
    handledFocusRef,
  ])

  const preferredLine = focusedLine ?? ownerLine ?? (gutter.anchorRange ? gutter.anchorRange.start.line + 1 : null)
  // Keyboard entry ignores read-ahead so it doesn't jump after fast scrolling: an anchor line only keeps the tab stop
  // in the window without lead, and the fallback is the block in view. Focused and action-owned rows stay mounted.
  const candidates =
    focusedLine == null && ownerLine == null
      ? Array.from({length: Math.max(0, entryLast - entry + 1)}, (_, index) => entry + index)
      : mounted
  const tabStopLine =
    preferredLine !== null && candidates.some(index => blocks[index]?.lineNumbers.includes(preferredLine - 1))
      ? preferredLine
      : (blocks[entry]?.lineNumbers[0] ?? -1) + 1
  return (
    <div
      ref={attachChunks}
      className={clsx(styles.WindowChunks, styles.InverseStickyChunks)}
      style={{height: initialHeight}}
    >
      {mounted.map(index => {
        const block = blocks[index]
        if (!block) return null
        return (
          <div
            key={block.anchor}
            className={styles.PositionedChunk}
            data-layer={layered.has(block.anchor) || undefined}
            style={{
              height: Math.max(1, block.lineNumbers.length) * lineHeight,
              transform: `translateY(${block.displayStart * lineHeight}px)`,
            }}
          >
            <SourceChunk
              virtualIndex={index}
              directDomUpdates={false}
              windowLocal
              layout={block}
              block={view?.blocks[index]}
              renderer={renderer}
              safariCSSHighlighting={safariCSSHighlighting}
              gutter={gutter}
              lineHeight={lineHeight}
              tabStopLine={block.lineNumbers.includes(tabStopLine - 1) ? tabStopLine : null}
              onFocusChange={onFocusChange}
              onRenderError={onRenderError}
              paintReady={controller !== null && preparedFor === controller}
              fallbackStyle={fallbackStyle}
              showGutter
            />
          </div>
        )
      })}
    </div>
  )
}
